import React from 'react';
import PropTypes from 'prop-types';
import { MentionsInput, Mention } from 'react-mentions';
import './MentionableTextArea.scss';

class MentionableTextArea extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			shouldRecalculateMaxLength: false,
			maxLength: 0,
			isAddingMention: false,
		};

		this.mentionRef = React.createRef();
	}

	componentDidMount = () => {
		if (this.props.maxLength) {
			this.setState({ maxLength: this.props.maxLength });
		}
	};

	componentDidUpdate = (prevProps) => {
		const previouslyNoUsers = !prevProps.data || prevProps.data.length <= 0;
		const nowUsers = this.props.data && this.props.data.length > 0;

		if (previouslyNoUsers && nowUsers && this.state.isAddingMention) {
			/**
			 * HACK: This appears to be the black magic required to trick the
			 * react-mentions component into showing its list of suggestions when
			 * the user is not actively typing.
			 *
			 * When the user has started to add a mention, but the API hasn't
			 * yet given us the list of mentionable users, we change the cursor
			 * over the textarea to a wait cursor.  If the user then stops typing
			 * to wait for that to go away, when the users finally arrive from
			 * the API, we want to show the list of suggestions immediately, not
			 * just clear the wait cursor and make the user figure out they have
			 * to start typing again.
			 *
			 * It turns out, that's difficult to accomplish, and removing any of
			 * the black magic "blur/select/click/unselect all/forceupdate"
			 * sequence below causes it to not happen.
			 */
			this.mentionRef.current.blur();
			this.mentionRef.current.select();
			this.mentionRef.current.click();
			this.mentionRef.current.selectionStart = this.props.value.length;
			this.mentionRef.current.selectionEnd = this.props.value.length;
			this.forceUpdate();
		}
	};

	calculateMaxLength = () => {
		if (!this.state.shouldRecalculateMaxLength) {
			return;
		}

		let markupLength = 0;
		// The characters that surround the name portion of a mention's markup
		const nameMarkupLength = '@[]'.length;
		// RegEx to match the entire mention syntax in the given value
		const mentionRegEx = /(@\[.+?\]\(.+?\))/g;
		// RegEx to match the parenthesis-wrapped user ID value inside the mention
		const idRegEx = /\(\d+\)/g;

		if (this.props.value && this.props.value.length > 0) {
			const matches = this.props.value.match(mentionRegEx);
			if (matches && matches.length > 0 && matches.length > 0) {
				markupLength = matches.reduce((total, mention) => {
					let idLength = 0;
					const idMatches = mention.match(idRegEx);
					if (idMatches && idMatches.length === 1) {
						// Id Value is an integer wrapped in parens.
						// For example: (45) would give an idLength of 4
						const idValue = idMatches[0];
						idLength = idValue.length;
					}
					return total + nameMarkupLength + idLength;
				}, 0);
			}
		}

		const maxLength = this.props.maxLength - markupLength;
		this.setState({ shouldRecalculateMaxLength: false, maxLength: maxLength });
	};

	/**
	 * Only recalculate max length when a mention has been added.  Here we set
	 * state to indicate that a recalc should occur on the next render.  If we
	 * try to calculate it now, we don't have the latest value with this mention's
	 * markup added to it.
	 */
	onAdd = () => {
		this.setState({ shouldRecalculateMaxLength: true, isAddingMention: false });
	};

	onChange = (event, newValue, newPlainTextValue) => {
		const firstCharIsAt = newPlainTextValue.length === 1 && newPlainTextValue[0] === '@';
		const lastCharIsAt = newPlainTextValue.length > 1 && newPlainTextValue[newPlainTextValue.length - 1] === '@';
		if (firstCharIsAt || lastCharIsAt) {
			this.setState({ isAddingMention: true });
		}
		// It turns out event isn't actually a real event,
		// so add the name value to its target object
		const namedEvent = {
			...event,
			target: {
				...event.target,
				name: this.props.name,
			},
		};
		this.props.onChange(namedEvent);
	};

	escapeRegExp = (string) => {
		return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
	};

	/**
	 * This function is used to completely rerender the highlighted
	 * suggestion, by cloning the React element that MentionsInput has already
	 * built, and modifying the text value of one of its children to be the
	 * full name and email address instead of just the name.
	 *
	 * This allows the markup and the display value of added mention to just
	 * include the user's full name, without the email.
	 */
	renderSuggestion = (entry, search, highlightedDisplay) => {
		const { children, ...otherProps } = { ...highlightedDisplay.props };
		const newChildren = [...children];
		if (newChildren && newChildren.length === 3) {
			/**
			 * Strip the part of the display name before the highlight and the
			 * search term itself (which would be highlighted) from the
			 * suggestion, and send that as the "after hightlight" child
			 */
			const priorToHighlight = newChildren[0];
			const escapedSearch = this.escapeRegExp(priorToHighlight) + this.escapeRegExp(search); // Escape special characters
			const upToEndOfHighlight = escapedSearch;
			const regex = new RegExp(upToEndOfHighlight, 'i');
			const newAfterHighlight = entry.suggestion.replace(regex, '');

			newChildren[2] = newAfterHighlight;

			let newDisplay = React.cloneElement(highlightedDisplay, otherProps, newChildren);
			return newDisplay;
		}
	};

	render() {
		this.calculateMaxLength();

		// Show wait cursor over mention text area if trying to add a mention, but
		// we don't yet have all the data loaded yet.
		let className = 'mentionable-text-area form-control';
		if (this.state.isAddingMention && this.props.data && this.props.data.length <= 0) {
			className = className + ' is-waiting';
			this.mentionRef.current.focus(); // Trigger focus so cursor re-renders as waiting
		}

		return (
			<div>
				<span className='has-float-label'>
					<MentionsInput
						placeholder={this.props.placeHolder || ''}
						className={className}
						value={this.props.value}
						onChange={this.onChange}
						name={this.props.name}
						maxLength={this.state.maxLength}
						allowSpaceInQuery={true}
						autoComplete='off'
						inputRef={this.mentionRef}>
						<Mention trigger='@' data={this.props.data} onAdd={this.onAdd} renderSuggestion={this.renderSuggestion} />
					</MentionsInput>
				</span>
			</div>
		);
	}
}

MentionableTextArea.propTypes = {
	name: PropTypes.string.isRequired,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	data: PropTypes.array.isRequired,
	maxLength: PropTypes.number,
	placeHolder: PropTypes.string,
};

export default MentionableTextArea;
